import React, { useEffect, useState } from 'react';
import { Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnapshot } from 'valtio';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { AuthStore } from '@/utils/store/auth';
import { supabaseAuthStore } from '@/utils/store/supabaseAuth';

const AuthLayout: React.FC = () => {
  // 只使用 Supabase 认证状态
  const supabaseAuth = useSnapshot(supabaseAuthStore);
  const { data: authData, isLoading: authLoading } = useSnapshot(AuthStore);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState('analysis');

  // 权限检查
  useEffect(() => {
    if (supabaseAuth.isAuthenticated && supabaseAuth.user?.email) {
      AuthStore.runFetch(supabaseAuth.user.email);
    }
  }, [supabaseAuth.isAuthenticated, supabaseAuth.user?.email]);

  // 根据当前路径设置选中的菜单项
  useEffect(() => {
    if (location.pathname.includes('/debug')) {
      setSelectedKey('debug');
    } else if (location.pathname.includes('/iframe')) {
      setSelectedKey('iframe');
    } else if (location.pathname.includes('/realtime')) {
      setSelectedKey('realtime');
    } else if (location.pathname.includes('/analysis')) {
      setSelectedKey('analysis');
    }
  }, [location.pathname]);

  // 如果正在加载权限信息或者还没有权限数据，显示鉴权中
  if (authLoading || authData === null) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg mb-4"></div>
        <div className="text-lg text-gray-600">鉴权中...</div>
      </div>
    );
  }

  // 如果不是超级管理员，显示无权限页面
  if (!authData.isSuperAdmin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，您没有权限访问此页面。只有超级管理员才能查看历史记录分析。"
        icon={<LockOutlined />}
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            返回上一页
          </Button>
        }
      />
    );
  }

  const handleMenuClick = (key: string) => {
    setSelectedKey(key);
    if (key === 'analysis') {
      navigate('/analysis');
    } else if (key === 'iframe') {
      navigate('/analysis/iframe');
    } else if (key === 'realtime') {
      navigate('/analysis/realtime');
    } else if (key === 'debug') {
      navigate('/analysis/debug');
    }
  };

  // 有权限，渲染带菜单栏的布局
  return (
    <div className="flex flex-col gap-4">
      {/* DaisyUI Navbar */}
      <div className="navbar bg-base-100 border-b border-base-200 px-6 h-10 rounded-xl">
         <div className="navbar-start flex items-center">
           <div className="text-lg font-bold text-primary mr-8">
             管理后台
           </div>
           <div className="tabs tabs-boxed bg-base-200">
            <button 
              className={`tab ${selectedKey === 'analysis' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('analysis')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              数据分析
            </button>
            <button 
              className={`tab ${selectedKey === 'realtime' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('realtime')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              语音转文字
            </button>
            <button 
              className={`tab ${selectedKey === 'iframe' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('iframe')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m-9 9a9 9 0 019-9" />
              </svg>
              腾讯BI
            </button>
            <button 
              className={`tab ${selectedKey === 'debug' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('debug')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              工作流调试
            </button>
          </div>
        </div>
        <div className="navbar-end">
          {/* 可以在这里添加用户信息或其他操作按钮 */}
        </div>
      </div>
      
      {/* 主要内容区域 */}
      <div className="">
        <Outlet />
      </div>
    </div>
  );
};

export default AuthLayout;